import { ChatMessageType, UnitOfMeasure } from "../contracts";
import { isRecord } from "../utils/objectValueConversion";
import type { ChatMessageViewModel } from "./chatMessageViewModel";

export enum CommercialSimulatorPlanType {
    Sku = "sku",
    SubBrand = "sub_brand"
}

export type ToolRequest = {
    type: "tool_use";
    plan_type: CommercialSimulatorPlanType;
    name: string;
    id: string;
};

export const CommercialSimulatorToolName = "commercial_simulator" as const;

export type CommercialSimulatorToolName = typeof CommercialSimulatorToolName;

export type CommercialSimulatorOptions = {
    sku_id: number;
    sku_name: string;
    brand_id: number;
    sub_brand_id: number;
    retailer_id: number;
    retailer_name: string;
};

export type ProductSelection = {
    id: number;
    name: string;
    retailer_id: number;
    retailer_name: string;
};

export type CommercialSimulatorSubBrandOptions = {
    products: ProductSelection[];
    retailer_id: number;
    retailer_name: string;
    sub_brand_name: string;
    brand_id: number;
    sub_brand_id: number;
    unit: UnitOfMeasure;
    unit_size: number;
    units_per_pack: number;
    pack_size_key: string;
};

export type CommercialSimulatorToolInputRetailer = {
    retailer_id?: number;
    retailer_name?: string;
};

export type CommercialSimulatorProductName = {
    base_name: string;
    quantity_1: number | null;
    unit_of_measurement_1: number | null;
    quantity_2: number | null;
    unit_of_measurement_2: number | null;
};

export type CommercialSimulatorToolInput = {
    product_name?: CommercialSimulatorProductName | null;
    product_id?: number | null;
    pack_size_key?: string | null;
    sub_brand_id?: number | null;
    retailers?: CommercialSimulatorToolInputRetailer[];
    objective?: string;
    plan_budget?: string;
    plan_requirements?: string[];
    plan_time_period_info?: string;
    reference_commercial_plan_id?: number | null;
};

export type CommercialSimulatorSkuToolRequest = ToolRequest & {
    plan_type: CommercialSimulatorPlanType.Sku;
    name: CommercialSimulatorToolName;
    input: CommercialSimulatorToolInput;
};

export type CommercialSimulatorSubBrandToolRequest = ToolRequest & {
    plan_type: CommercialSimulatorPlanType.SubBrand;
    name: CommercialSimulatorToolName;
    input: CommercialSimulatorToolInput;
};

export type CommercialSimulatorToolRequest = CommercialSimulatorSkuToolRequest | CommercialSimulatorSubBrandToolRequest;

export const isCommercialSimulatorToolRequest = (
    payload: unknown
): payload is CommercialSimulatorToolRequest => {
    if (!isRecord(payload) || !isRecord(payload.input)) {
        return false;
    }
    return (
        payload.type === "tool_use"
        && typeof payload.id === "string"
        && payload.name === CommercialSimulatorToolName
        && (
            payload.plan_type === CommercialSimulatorPlanType.Sku
            || payload.plan_type === CommercialSimulatorPlanType.SubBrand
        )
    );
};

export const isCommercialSimulatorSkuToolRequest = (
    payload: unknown
): payload is CommercialSimulatorSkuToolRequest => {
    return (
        isCommercialSimulatorToolRequest(payload)
        && payload.plan_type === CommercialSimulatorPlanType.Sku
    );
};

export const isCommercialSimulatorSubBrandToolRequest = (
    payload: unknown
): payload is CommercialSimulatorSubBrandToolRequest => {
    return (
        isCommercialSimulatorToolRequest(payload)
        && payload.plan_type === CommercialSimulatorPlanType.SubBrand
    );
};

export const isToolRequest = (
    type: ChatMessageType | null | undefined,
    payload: unknown | null | undefined
): payload is ToolRequest => {
    if (!isRecord(payload) || type !== ChatMessageType.ToolRequest) {
        return false;
    }

    return (
        payload.type === "tool_use"
        && typeof payload.id === "string"
        && typeof payload.name === "string"
        && (
            payload.plan_type === CommercialSimulatorPlanType.Sku
            || payload.plan_type === CommercialSimulatorPlanType.SubBrand
        )
    );
};

export const getToolRequestMessageById = <TToolResponse extends ToolRequest = ToolRequest>(
    messages: ChatMessageViewModel[] | undefined,
    toolRequestId: string | undefined
): { message: ChatMessageViewModel; payload: TToolResponse } | undefined => {
    if (!messages || !toolRequestId) {
        return undefined;
    }

    for (const message of messages) {
        const payload = message.payload;

        if (isToolRequest(message.type, payload) && payload.id === toolRequestId) {
            return { message, payload: payload as TToolResponse };
        }
    }

    return undefined;
};

export const getToolRequestById = <TToolResponse extends ToolRequest = ToolRequest>(
    messages: ChatMessageViewModel[] | undefined,
    toolRequestId: string | undefined
): TToolResponse | undefined => {
    if (!messages || !toolRequestId) {
        return undefined;
    }

    for (const message of messages) {
        const payload = message.payload;

        if (isToolRequest(message.type, payload) && payload.id === toolRequestId) {
            return payload as TToolResponse;
        }
    }

    return undefined;
};

export const getLastToolRequest = (
    messages: ChatMessageViewModel[] | undefined
): ChatMessageViewModel | undefined => {
    if (!messages) {
        return undefined;
    }

    for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];

        if (message.type === ChatMessageType.ToolRequest) {
            return message;
        }
    }

    return undefined;
};

export const isCurrentMessageToolRequest = (
    messages: ChatMessageViewModel[] | undefined
): boolean => {
    const lastMessage = [...(messages ?? [])]
        .sort((a, b) => (b.sequence ?? 0) - (a.sequence ?? 0))
        .at(0);

    return lastMessage?.type === ChatMessageType.ToolRequest;
};
